<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
<ims-modal :active="active" :title="$t('delete-annotations-title')" @close="close">
  <div class="delete-annotations-body">
    <b-field>
      <b-radio-button v-model="mode" native-value="per-user" type="is-link">
        {{ $t('delete-annotations-mode-per-user') }}
      </b-radio-button>
      <b-radio-button v-model="mode" native-value="all" type="is-danger">
        {{ $t('delete-annotations-mode-all') }}
      </b-radio-button>
    </b-field>

    <b-field v-if="mode === 'per-user'" :label="$t('delete-annotations-select-users')">
      <domain-tag-input
        v-model="selectedUsers"
        :domains="members"
        placeholder="search-user"
        displayedProperty="fullName"
        searchedProperty="fullName"
      />
    </b-field>

    <p v-else class="all-warning">
      {{ $t('delete-annotations-all-warning') }}
    </p>
  </div>

  <template #footer>
    <button class="button" @click="close">
      {{ $t('button-cancel') }}
    </button>
    <button class="button is-danger" :disabled="!canDelete" @click="confirm">
      {{ $t('button-delete') }}
    </button>
  </template>
</ims-modal>
</template>

<script>
import {Ims} from '@/api';
import ImsModal from '@/components/utils/ImsModal';
import DomainTagInput from '@/components/utils/DomainTagInput';

export default {
  name: 'delete-annotations-modal',
  components: {
    ImsModal,
    DomainTagInput,
  },
  props: {
    active: {type: Boolean, default: false},
    image: {type: Object, required: true}
  },
  data() {
    return {
      mode: 'per-user',
      selectedUsers: []
    };
  },
  computed: {
    members() {
      return this.$store.state.currentProject.members || [];
    },
    canDelete() {
      return this.mode === 'all' || this.selectedUsers.length > 0;
    }
  },
  watch: {
    active(val) {
      if (val) {
        this.mode = 'per-user';
        this.selectedUsers = [];
      }
    }
  },
  methods: {
    close() {
      this.$emit('update:active', false);
    },
    confirm() {
      if (!this.canDelete) {
        return;
      }
      let allUsers = this.mode === 'all';
      let users = allUsers
        ? this.$t('delete-annotations-all-users')
        : this.selectedUsers.map(u => u.fullName).join(', ');
      this.$buefy.dialog.confirm({
        title: this.$t('delete-annotations-title'),
        message: this.$t('delete-annotations-confirm', {users}),
        type: 'is-danger',
        confirmText: this.$t('button-confirm'),
        cancelText: this.$t('button-cancel'),
        onConfirm: () => this.deleteAnnotations(allUsers)
      });
    },
    async deleteAnnotations(allUsers) {
      try {
        let payload = {
          image: this.image.id,
          allUsers,
          users: allUsers ? [] : this.selectedUsers.map(u => u.id)
        };
        let {data} = await Ims.instance.api.post('annotation/delete-batch.json', payload);
        this.$notify({
          type: 'success',
          text: this.$tc('notif-success-annotations-deletion-scheduled', data.scheduled, {count: data.scheduled})
        });
        this.$emit('deleted');
        this.close();
      } catch (error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('notif-error-annotations-deletion')});
      }
    }
  }
};
</script>

<style scoped>
.delete-annotations-body {
  min-height: 8rem;
}

.all-warning {
  color: #b60000;
}

>>> .modal-card, >>> .modal-card-body {
  overflow: visible !important;
  width: 50vw !important;
}
</style>
